import axios from "axios";
import { GeneratedPost, Platform } from "../types";
import API from "../services/api";
import { post } from "node_modules/axios/index.cjs";
import Cookies from "js-cookie";

// Facebook
export async function postToFacebook(
  pageId: string,
  accessToken: string,
  post: GeneratedPost
) {
  const url = `https://graph.facebook.com/${pageId}/feed`;
  const data: any = {
    message: `${post.caption}\n${post.hashtags.join(" ")}`,
    access_token: accessToken,
  };
  if (post.imageUrl) data.picture = post.imageUrl;
  return axios.post(url, data);
}

// Instagram
export async function postToInstagram(
  businessAccountId: string,
  accessToken: string,
  post: GeneratedPost
) {
  if (!post.imageUrl) throw new Error("Instagram post requires imageUrl");
  // Step 1: Create media object
  const mediaRes = await axios.post(
    `https://graph.facebook.com/v19.0/${businessAccountId}/media`,
    {
      image_url: post.imageUrl,
      caption: post.caption,
      access_token: accessToken,
    }
  );
  // Step 2: Publish media
  return axios.post(
    `https://graph.facebook.com/v19.0/${businessAccountId}/media_publish`,
    { creation_id: mediaRes.data.id, access_token: accessToken }
  );
}

// LinkedIn
export async function postToLinkedIn(
  organizationId: string,
  accessToken: string,
  post: GeneratedPost
) {
  const url = "https://api.linkedin.com/v2/ugcPosts";
  const data = {
    author: `urn:li:organization:${organizationId}`,
    lifecycleState: "PUBLISHED",
    specificContent: {
      "com.linkedin.ugc.ShareContent": {
        shareCommentary: { text: post.caption },
        shareMediaCategory: post.imageUrl ? "IMAGE" : "NONE",
        media: post.imageUrl
          ? [{ status: "READY", originalUrl: post.imageUrl }]
          : [],
      },
    },
    visibility: { "com.linkedin.ugc.MemberNetworkVisibility": "PUBLIC" },
  };
  return axios.post(url, data, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function postToLinkedInFromServer(
  accessToken: string,
  post: GeneratedPost,
  pageId?: string
) {
  try {
    console.log("pageId", pageId);
    const response = await API.linkedinPost({
      accessToken,
      post,
      ...(pageId && { pageId }),
    });

    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || error.message);
  }
}

export async function postToFacebookFromServer(
  accessToken: string,
  post: GeneratedPost,
  pageId?: string
) {
  try {
    const response = await API.facebookPost({ accessToken, post, pageId });

    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || error.message);
  }
}

export async function postToInstagramFromServer(
  accessToken: string,
  post: GeneratedPost
) {
  try {
    if (!post.imageUrl) {
      throw new Error("Instagram post requires an image");
    }

    console.log("📸 Instagram posting with:", {
      hasImage: !!post.imageUrl,
      caption: post.caption?.substring(0, 50) + "...",
      hashtags: post.hashtags?.length || 0,
    });

    const response = await API.instagramPost({ accessToken, post });

    return response.data;
  } catch (error: any) {
    console.error("Instagram posting error:", error.response?.data);
    throw new Error(error.response?.data?.error || error.message);
  }
}

export async function postToYouTubeFromServer(
  accessToken: string,
  post: GeneratedPost,
  videoUrl?: string,
  thumbnailUrl?: string
) {
  try {
    if (!videoUrl && !post.imageUrl) {
      throw new Error("YouTube requires a video file");
    }

    const videoUrlToUse = videoUrl || post.imageUrl;

    // Optimize thumbnail: resize to 1280x720 and compress to < 2 MiB
    let optimizedThumbnailUrl = thumbnailUrl;
    if (thumbnailUrl) {
      // optimizedThumbnailUrl = await optimizeThumbnail(thumbnailUrl);
      optimizedThumbnailUrl = thumbnailUrl;
    }

    console.log("📹 YouTube posting with:", {
      videoUrl: videoUrlToUse?.substring(0, 50) + "...",
      hasThumbnail: !!optimizedThumbnailUrl,
      thumbnailUrl: optimizedThumbnailUrl?.substring(0, 50) + "...",
    });

    const response = await API.youtubePost({
      accessToken,
      post: {
        ...post,
        thumbnailUrl: optimizedThumbnailUrl,
      },
      videoUrl: videoUrlToUse,
    });

    const videoId = response.data?.videoId;
    console.log("✅ YouTube video uploaded successfully, videoId:", videoId);

    // Step 2: Upload custom thumbnail if available and not already set
    if (optimizedThumbnailUrl && videoId) {
      console.log("🎨 Uploading custom thumbnail for YouTube video:", videoId);
      try {
        const thumbnailResponse = await API.youtubeSetThumbnail({
          accessToken,
          videoId,
          thumbnailUrl: optimizedThumbnailUrl,
        });

        if (thumbnailResponse.data?.success) {
          console.log("✅ YouTube thumbnail uploaded successfully");
          // Add thumbnail info to response
          response.data.thumbnailUploaded = true;
          response.data.thumbnailMessage = thumbnailResponse.data.message;
        } else {
          console.warn(
            "⚠️ YouTube thumbnail upload failed:",
            thumbnailResponse.data?.error
          );
          response.data.thumbnailUploaded = false;
          response.data.thumbnailError =
            thumbnailResponse.data?.error || "Unknown thumbnail upload error";
        }
      } catch (thumbnailError: any) {
        console.warn(
          "⚠️ YouTube thumbnail upload failed:",
          thumbnailError.message
        );
        // Don't fail the entire upload if thumbnail fails, just log it
        response.data.thumbnailUploaded = false;
        response.data.thumbnailError =
          thumbnailError.response?.data?.error || thumbnailError.message;
      }
    } else if (!optimizedThumbnailUrl) {
      console.log("🎨 No thumbnail URL provided, skipping thumbnail upload");
    } else if (!videoId) {
      console.warn("⚠️ No video ID available for thumbnail upload");
    }

    return response.data;
  } catch (error: any) {
    // Improve error message for quota limits
    const errorMessage = error.response?.data?.error || error.message;

    if (
      errorMessage.includes("upload limit exceeded") ||
      errorMessage.includes("exceeded the number of videos")
    ) {
      throw new Error(
        "YouTube daily upload limit exceeded. Try again tomorrow after midnight PT."
      );
    }

    throw new Error(errorMessage);
  }
}

/**
 * Optimize thumbnail: resize to 1280x720 and compress to < 2 MiB
 */
export async function optimizeThumbnail(
  imageSrc: string | Blob,
  targetWidth: number,
  targetHeight: number,
  maxFileSize: number,
  quality: number = 0.85,
  output: "blob" | "objectUrl" = "blob"
): Promise<Blob | string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    let objectUrl: string | null = null;

    if (imageSrc instanceof Blob) {
      objectUrl = URL.createObjectURL(imageSrc);
      img.src = objectUrl;
    } else {
      if (
        imageSrc.startsWith("http") &&
        !imageSrc.startsWith("data:") &&
        !imageSrc.startsWith("blob:")
      ) {
        img.crossOrigin = "anonymous";
      }
      img.src = imageSrc;
    }

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Failed to get canvas context"));

      const scale = Math.min(
        targetWidth / img.width,
        targetHeight / img.height
      );

      const x = (targetWidth - img.width * scale) / 2;
      const y = (targetHeight - img.height * scale) / 2;

      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

      let currentQuality = quality;

      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Failed to create blob"));

            if (blob.size <= maxFileSize) {
              if (objectUrl) URL.revokeObjectURL(objectUrl);
              resolve(output === "blob" ? blob : URL.createObjectURL(blob));
            } else if (currentQuality > 0.3) {
              currentQuality -= 0.1;
              tryCompress();
            } else {
              reject(
                new Error(
                  `Could not compress below ${(
                    maxFileSize /
                    1024 /
                    1024
                  ).toFixed(1)} MiB`
                )
              );
            }
          },
          "image/jpeg",
          currentQuality
        );
      };

      tryCompress();
    };

    img.onerror = () => reject(new Error("Failed to load image"));
  });
}

export async function postToLinkedInPersonal(
  accessToken: string,
  post: GeneratedPost
) {
  // Step 1: Get personId from LinkedIn
  const meResponse = await fetch(
    `/api/linkedin/me?access_token=${accessToken}`,
    {}
  );

  const meData = await meResponse.json();
  if (!meResponse.ok) {
    throw new Error(
      `Failed to get LinkedIn person ID: ${
        meData.message || meResponse.statusText
      }`
    );
  }

  const personId = meData.id;

  // Step 2: Prepare post data
  const url = "https://api.linkedin.com/v2/ugcPosts";
  const data = {
    author: `urn:li:person:${personId}`,
    lifecycleState: "PUBLISHED",
    specificContent: {
      "com.linkedin.ugc.ShareContent": {
        shareCommentary: { text: post.caption },
        shareMediaCategory: post.imageUrl ? "IMAGE" : "NONE",
        media: post.imageUrl
          ? [{ status: "READY", originalUrl: post.imageUrl }]
          : [],
      },
    },
    visibility: {
      "com.linkedin.ugc.MemberNetworkVisibility": "PUBLIC",
    },
  };

  // Step 3: Send post request
  return axios.post(url, data, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "X-Restli-Protocol-Version": "2.0.0",
      "Content-Type": "application/json",
    },
  });
}

// Placeholder for Twitter, TikTok, YouTube
// Twitter/X
export async function postToTwitter(params: {
  accessToken: string;
  post: GeneratedPost;
}) {
  const url = "https://api.twitter.com/2/tweets";

  const tweetText = `${params.post.caption}\n\n${params.post.hashtags.join(
    " "
  )}`;

  const data: any = {
    text: tweetText.slice(0, 280), // Twitter character limit
  };

  // Add media if image URL is provided
  if (params.post.imageUrl) {
    // First upload media
    const mediaId = await uploadTwitterMedia(
      params.accessToken,
      params.post.imageUrl
    );
    if (mediaId) {
      data.media = { media_ids: [mediaId] };
    }
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${params.accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new SocialPosterError(
      `Twitter post failed: ${error.detail || error.title || "Unknown error"}`,
      "twitter",
      response.status,
      response.status >= 500
    );
  }

  return response.json();
}

// TikTok

export async function postToTikTok(params: {
  accessToken: string;
  post: GeneratedPost;
}) {
  if (!params.post.imageUrl) {
    throw new SocialPosterError(
      "TikTok requires a video file",
      "tiktok",
      400,
      false
    );
  }

  try {
    const res = await API.tiktokUploadInit({
      accessToken: params.accessToken,
      post: {
        postId: params.post.postId,
        tiktok_title: params.post.tiktok_title,
        tiktok_description: params.post.tiktok_description,
        caption: params.post.caption,
        hashtags: params.post.hashtags,
        video_url: params.post.imageUrl,
      },
    });
    return res;
  } catch (error) {
    console.error("TikTok post error:", error);
    throw error;
  }
}

export async function postToYouTube(params: {
  accessToken: string;
  post: GeneratedPost;
  videoUrl?: string;
}) {
  if (!params.videoUrl) {
    throw new SocialPosterError(
      "YouTube requires a video file",
      "youtube",
      400,
      false
    );
  }

  try {
    // Use the server API for YouTube posting
    const response = await fetch("/api/youtube/upload-init", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        accessToken: params.accessToken,
        post: params.post,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new SocialPosterError(
        `YouTube upload init failed: ${error.error || "Unknown error"}`,
        "youtube",
        response.status,
        response.status >= 500
      );
    }

    const initData = await response.json();

    // Upload the video using the server API
    const uploadResponse = await fetch("/api/youtube/upload-video", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        accessToken: params.accessToken,
        uploadUrl: initData.uploadUrl,
        videoUrl: params.videoUrl,
      }),
    });

    if (!uploadResponse.ok) {
      const error = await uploadResponse.json();
      throw new SocialPosterError(
        `YouTube video upload failed: ${error.error || "Unknown error"}`,
        "youtube",
        uploadResponse.status,
        uploadResponse.status >= 500
      );
    }

    return uploadResponse.json();
  } catch (error) {
    if (error instanceof SocialPosterError) {
      throw error;
    }
    throw new SocialPosterError(
      `YouTube posting failed: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      "youtube",
      500,
      true
    );
  }
}

export async function postToAllPlatforms(
  posts: GeneratedPost[],
  onProgress?: (
    platform: string,
    status: "pending" | "success" | "error"
  ) => void,
  context?: {
    linkedinPageId?: string;
    facebookPageId?: string;
    youtubeChannelId?: string;
    thumbnailUrl?: string;
  }
): Promise<Record<string, any>> {
  const results: Record<string, any> = {};
  const errors: string[] = [];
  const successes: string[] = [];

  const token = Cookies.get("auth_token");
  if (!token) {
    throw new Error("Authentication token not found. Please log in again.");
  }

  for (const post of posts) {
    try {
      onProgress?.(post.platform, "pending");

      let realPostResult: any = null;
      try {
        realPostResult = await postWithRealOAuth(post, "", context);
      } catch (error) {
        throw error;
      }

      if (realPostResult?.success) {
        results[post.platform] = {
          success: true,
          data: realPostResult,
          method: "real",
          message:
            realPostResult.message || `Successfully posted to ${post.platform}`,
          postId: realPostResult.postId || "Unknown",
          postUrl: realPostResult.postUrl || "Unknown",
          username: realPostResult.username || "Unknown",
        };
        successes.push(post.platform);
        onProgress?.(post.platform, "success");

      
      } else if (realPostResult && !realPostResult.success) {
        const errorMessage =
          realPostResult.error ||
          realPostResult.message ||
          `Failed to post to ${post.platform}`;
        throw new Error(errorMessage);
      } else {
        throw new Error(`No result returned from ${post.platform} posting`);
      }
    } catch (error: any) {
      const errorMessage =
        error.message || `Failed to post to ${post.platform}`;
      console.error(`Failed to post to ${post.platform}:`, error);

      results[post.platform] = {
        success: false,
        error: errorMessage,
        platform: post.platform,
        retryable:
          !errorMessage.includes("connect") &&
          !errorMessage.includes("expired"),
      };
      errors.push(`${post.platform}: ${errorMessage}`);
      onProgress?.(post.platform, "error");
    }

    // Add delay between posts (except after the last one)
    if (posts.indexOf(post) < posts.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  // Add summary
  results._summary = {
    total: posts.length,
    successful: successes.length,
    failed: errors.length,
    errors: errors,
    successes: successes,
  };

  return results;
}

async function postWithRealOAuth(
  post: GeneratedPost,
  accessToken: string,
  context?: {
    facebookPageId?: string;
    youtubeChannelId?: string;
    linkedinPageId?: string;
    thumbnailUrl?: string;
  }
): Promise<{
  success: boolean;
  message: string;
  postId?: string;
  postUrl?: any;
  video_id?: string;
  username?: string;
}> {
  try {
    switch (post.platform) {
      case "linkedin":
        const result: any = await postToLinkedInFromServer(
          accessToken,
          post,
          context?.linkedinPageId || post.pageId
        );
        return {
          success: true,
          message: `Successfully posted to LinkedIn`,
          postId: result?.postId,
          postUrl: result?.postUrl,
        };

      case "facebook":
        const fbResult = await postToFacebookFromServer(
          accessToken,
          post,
          context?.facebookPageId || post.pageId
        );

        let fbPostId =
          fbResult?.postId ||
          fbResult?.id ||
          fbResult?.data?.id ||
          fbResult?.post_id;

        if (!fbPostId && fbResult?.data) {
          fbPostId =
            fbResult.data.postId || fbResult.data.id || fbResult.data.post_id;
        }

        return {
          success: true,
          message: `Successfully posted to Facebook`,
          postId: fbPostId || "Unknown",
          postUrl: fbResult?.data.postUrl,
        };

      case "youtube":
        const thumbnailUrl =
          context?.thumbnailUrl || (post as any).thumbnailUrl;
        const ytResult = await postToYouTubeFromServer(
          accessToken,
          post,
          post.imageUrl,
          thumbnailUrl
        );

        let message = `Successfully posted to YouTube`;
        if (ytResult.thumbnailUploaded) {
          message += ` with custom thumbnail`;
        } else if (ytResult.thumbnailError) {
          message += ` (thumbnail upload failed: ${ytResult.thumbnailError})`;
        }

        return {
          success: true,
          message,
          postId: ytResult.videoId || ytResult.data?.id,
        };

      case "instagram":
        const igResult = await postToInstagramFromServer(accessToken, post);
        return {
          success: true,
          message: `Successfully posted to Instagram`,
          postId: igResult.postId || igResult.data?.id,
          username: igResult.username,
        };

      case "tiktok": {
        try {
          const videoUrl = post.mediaUrl || post.imageUrl;
          if (!videoUrl)
            throw new Error("Missing video URL for TikTok upload.");

          let params = {
            accessToken,
            post: { ...post, mediaUrl: videoUrl },
          };

          const result = await postToTikTok(params);
          return {
            success: true,
            message: `Successfully posted to TikTok`,
            postId: result?.data?.data?.data?.publish_id,
            username: result?.data?.data?.username,
            postUrl: result?.data?.data?.postUrl,
          };
        } catch (error: any) {
          throw new Error(`TikTok upload failed: ${error.message}`);
        }
      }

      default:
        throw new Error(`Unsupported platform: ${post.platform}`);
    }
  } catch (error: any) {
    console.error(`Real OAuth posting failed for ${post.platform}:`, error);
    return {
      success: false,
      message: error.message || `Failed to post to ${post.platform}`,
    };
  }
}

export class SocialPosterError extends Error {
  constructor(
    message: string,
    public platform: string,
    public statusCode?: number,
    public retryable: boolean = false
  ) {
    super(message);
    this.name = "SocialPosterError";
  }
}

// Retry mechanism for failed posts
async function withRetry<T>(
  fn: () => Promise<T>,
  platform: string,
  maxRetries: number = 2
): Promise<T> {
  let lastError: Error;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // Don't retry on authentication errors
      if (error instanceof SocialPosterError && !error.retryable) {
        throw error;
      }

      if (attempt === maxRetries) {
        throw new SocialPosterError(
          `Failed after ${maxRetries} attempts: ${lastError.message}`,
          platform,
          undefined,
          false
        );
      }

      // Exponential backoff
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, attempt) * 1000)
      );
    }
  }

  throw lastError!;
}

async function uploadTwitterMedia(
  accessToken: string,
  imageUrl: string
): Promise<string | null> {
  try {
    // Download image
    const imageResponse = await fetch(imageUrl);
    const imageBlob = await imageResponse.blob();
    const imageBuffer = await imageBlob.arrayBuffer();
    const base64Image = btoa(
      String.fromCharCode.apply(
        null,
        Array.from(new Uint8Array(imageBuffer)) as any
      )
    );

    // Upload to Twitter
    const uploadResponse = await fetch(
      "https://upload.twitter.com/1.1/media/upload.json",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          media_data: base64Image,
        }),
      }
    );

    if (uploadResponse.ok) {
      const result = await uploadResponse.json();
      return result.media_id_string;
    }
  } catch (error) {
    console.error("Failed to upload Twitter media:", error);
  }
  return null;
}

// Helper function to upload video to TikTok

// Helper functions to get platform-specific IDs
async function getFacebookPageId(accessToken: string): Promise<string> {
  const response = await fetch(
    `https://graph.facebook.com/me/accounts?access_token=${accessToken}`
  );
  const data = await response.json();

  if (!response.ok) {
    throw new SocialPosterError(
      `Failed to get Facebook page ID: ${data.error?.message}`,
      "facebook",
      response.status,
      false
    );
  }

  if (!data.data || data.data.length === 0) {
    throw new SocialPosterError(
      "No Facebook pages found",
      "facebook",
      404,
      false
    );
  }

  return data.data[0].id;
}

async function getInstagramBusinessAccountId(
  accessToken: string
): Promise<string> {
  const pageId = await getFacebookPageId(accessToken);
  const response = await fetch(
    `https://graph.facebook.com/${pageId}?fields=instagram_business_account&access_token=${accessToken}`
  );
  const data = await response.json();

  if (!response.ok) {
    throw new SocialPosterError(
      `Failed to get Instagram account ID: ${data.error?.message}`,
      "instagram",
      response.status,
      false
    );
  }

  if (!data.instagram_business_account) {
    throw new SocialPosterError(
      "No Instagram business account linked",
      "instagram",
      404,
      false
    );
  }

  return data.instagram_business_account.id;
}

async function getLinkedInOrganizationId(accessToken: string): Promise<string> {
  const response = await fetch(
    `/api/v2/organizationalEntityAcls?q=roleAssignee&role=ADMIN&access_token=${accessToken}`,
    {
      // headers: { Authorization: `Bearer ${accessToken}` }
    }
  );
  const data = await response.json();

  if (!response.ok) {
    throw new SocialPosterError(
      `Failed to get LinkedIn organization ID: ${data.message}`,
      "linkedin",
      response.status,
      false
    );
  }

  const org = data.elements?.find((el: any) =>
    el.organizationalTarget?.startsWith("urn:li:organization:")
  );
  if (!org) {
    throw new SocialPosterError(
      "No LinkedIn organizations found",
      "linkedin",
      404,
      false
    );
  }

  return org.organizationalTarget.split(":").pop();
}

// Helper function to extract post ID from API response
function getPostIdFromResult(result: any, platform: string): string {
  switch (platform) {
    case "facebook":
      return result.data?.id || result.id || "Unknown";
    case "instagram":
      return result.data?.id || result.id || "Unknown";
    case "linkedin":
      return result.data?.id || result.id || "Unknown";
    case "twitter":
      return result.data?.id || result.id || "Unknown";
    case "tiktok":
      return result.data?.publish_id || "Unknown";
    case "youtube":
      return result.id || "Unknown";
    default:
      return "Unknown";
  }
}


async function savePublishedPostToHistory(
  post: any,
  publishResult: any
): Promise<void> {
  try {
    // Reuse existing draft/post history id if provided to preserve original id
      const postId = post.postId || post.draftId || `${post.platform}-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
    let platformUrl = ""; 

    if (publishResult.postId && publishResult.postId !== "Unknown") {
      switch (post.platform) {
        case "linkedin":
          if (publishResult?.postUrl) {
            platformUrl = publishResult.postUrl;
          } else if (publishResult?.postId?.startsWith("urn:li:share:")) {
            platformUrl = `https://www.linkedin.com/feed/update/${publishResult.postId}`;
          } else if (publishResult.postId.includes("activity-")) {
            platformUrl = `https://www.linkedin.com/posts/${publishResult.postId}`;
          } else {
            platformUrl = `https://www.linkedin.com/feed/update/urn:li:share:${publishResult.postId}`;
          }
          break;
        case "facebook":
          if (publishResult?.postUrl) {
            platformUrl = publishResult?.postUrl;
          } else if (publishResult.postId?.includes("_")) {
            const [pageId, postIdOnly] = publishResult.postId.split("_");
            platformUrl = `https://www.facebook.com/${pageId}/posts/${postIdOnly}`;
          } else {
            platformUrl = `https://www.facebook.com/posts/${publishResult.postId}`;
          }
          break;
        case "youtube":
          platformUrl = `https://www.youtube.com/watch?v=${publishResult.postId}`;
          break;
        case "instagram":
          platformUrl = `${publishResult.postId}`;
          break;
        case "twitter":
        case "x":
          platformUrl = `https://twitter.com/user/status/${publishResult.postId}`;
          break;
        case "tiktok":
          if (publishResult?.postUrl) {
            platformUrl = publishResult.postUrl;
          } else {
            platformUrl = `https://www.tiktok.com/@${publishResult?.username}/video/${publishResult?.postId}`;
          }
          break;

        default:
          platformUrl = `https://${post.platform}.com/posts/${publishResult.postId}`;
          break;
      }
    } else {
      if (post.platform === "facebook") {
        platformUrl = "https://www.facebook.com";
      }
    }
    console.log("publishResult", publishResult);
    console.log("platformUrl", platformUrl);
    const publishedUrls = {
      [post.platform]: platformUrl,
    };

    const response = await API.savePublishedUrls({
      postId,
      postContent: post.content || post.caption,
      youtubeTitle: post?.youtubeTitle || "",
      youtubeDescription: post?.youtubeDescription || "",
      tiktok_title: post?.tiktok_title || "",
      tiktok_description: post?.tiktok_description || "",
      publishedUrls,
      platforms: [post.platform],
      category: "General",
      imageUrl: post.imageUrl || post.mediaUrl,
      status: "published",
    });

    await response.data.data;
  } catch (error: any) {
    throw error;
  }
}
